import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Event, EventPricing, EventPricingTicketType, TicketsRequest, TicketsResponse} from '../../../../generated/sales-terminal-client';
import {SelfServiceEventService} from '../../../../services/self-service/event/self-service-event.service';
import {RouteService} from '../../../../services/route/route.service';
import {finalize} from 'rxjs/operators';
import {CartService} from '../../../../services/cart/cart.service';
import {AuthService} from '../../../../services/auth/auth.service';
import {TicketTypeService} from '../../../../services/ticket-type/ticket-type.service';
import moment from 'moment/moment';
import {StorageService} from '../../../../services/storage/storage.service';
import {UtilService} from '../../../../services/util/util.service';
import {cloneDeep} from 'lodash-es';
import {Subscription} from 'rxjs';

@Component({
  selector: 'app-ticket-select-types',
  templateUrl: './ticket-select-types.component.html',
  styleUrls: ['./ticket-select-types.component.scss']
})
export class TicketSelectTypesComponent implements OnInit, OnDestroy {
  public static EVENT_PURCHASE_TICKET_TYPE_COUNT_MAP = 'eventPurchase.ticketTypeCountMap';
  @Input() public event: Event;
  @Output() public updateCart: EventEmitter<any> = new EventEmitter<any>();
  @Output() public openCustomerScreen: EventEmitter<any> = new EventEmitter<any>();

  public initialised: boolean;
  public get eventPricing(): EventPricing {
    return this.cartService?.eventPricing;
  }
  public get ticketTypeCountMap(): Record<string, number> {
    return this.ticketTypeService.ticketTypeCountMap;
  }
  public set ticketTypeCountMap(value: Record<string, number>) {
    this.ticketTypeService.ticketTypeCountMap = value;
  }

  public getMaxTickets(ticketType: EventPricingTicketType): number {
    return this.cartService.calculateMaxTickets(ticketType.available != null ? ticketType.available : this.eventPricing.available);
  }

  public hasEventSaleStarted: boolean;
  private _storageSub: Subscription;

  constructor(public routeService: RouteService,
              public ticketTypeService: TicketTypeService,
              private eventService: SelfServiceEventService,
              public cartService: CartService,
              public authService: AuthService,
              private storageService: StorageService) {
  }

  public ngOnInit(): void {
    this.ticketTypeCountMap = {};
    this.initialised = false;
    if (this.event.eventId) {
      this.initTypes(true);
      this._storageSub = this.storageService.changes.subscribe(({ key, value }) => {
        if (key === CartService.STORAGE_CART_UPDATE) {
          if (this.cartService.caller) {
            this.cartService.caller = false;
          } else {
            this.initTypes();
          }
        }
        if (key === TicketSelectTypesComponent.EVENT_PURCHASE_TICKET_TYPE_COUNT_MAP) {
          if (this.cartService.caller) {
            this.cartService.caller = false;
          } else {
            this.ticketTypeCountMap = UtilService.parseJSON(value);
          }
        }
      });
    }
  }
  public ngOnDestroy(): void {
    if (this._storageSub) {
      this._storageSub.unsubscribe();
    }
  }

  private initTypes(isInit?: boolean): void {
    this.ticketTypeService.typesLoading = true;
    this.eventService.getEventPricing(this.event.eventId).pipe(
      finalize(() => this.ticketTypeService.typesLoading = false)
    ).subscribe((eventPricing: EventPricing) => {
      const now: moment.Moment = moment.utc(new Date());
      const saleStart: moment.Moment = moment.utc(this.event.sale?.start || null);
      const saleEnd: moment.Moment = moment.utc(this.event.sale?.end || null);
      this.hasEventSaleStarted = saleStart.isSameOrBefore(now) && saleEnd.isSameOrAfter(now);
      this.cartService.eventPricing = eventPricing;
      this.ticketTypeService.resetTicketTypeCounts();
      if (isInit) {
        this.initialised = true;
      }
    });
  }

  public changeTicketTypeCount(typeUuid: string, remove?: boolean): void {
    if (remove) {
      this.ticketTypeCountMap[typeUuid]--;
    } else {
      this.ticketTypeCountMap[typeUuid]++;
    }
    this.updateCustomerScreen();
  }
  private updateCustomerScreen(): void {
    this.cartService.caller = true;
    this.storageService.set(TicketSelectTypesComponent.EVENT_PURCHASE_TICKET_TYPE_COUNT_MAP, JSON.stringify(cloneDeep(this.ticketTypeCountMap)));
  }

  public changeTicketCount(typeUuid: string, count: number): void {
    this.ticketTypeCountMap[typeUuid] = count >= 0 ? count : 0;
  }

  public addToCart(): void {
    const ticketsRequests: TicketsRequest[] = [];
    Object.keys(this.ticketTypeCountMap).forEach(ticketTypeUuid => {
      if (this.ticketTypeCountMap[ticketTypeUuid]) {
        ticketsRequests.push({ticketTypeUuid: ticketTypeUuid, quantity: this.ticketTypeCountMap[ticketTypeUuid]});
      }
    })
    if (ticketsRequests.length) {
      this.ticketTypeService.typesLoading = true;
      this.cartService.isLoading = true;
      this.cartService.setUnassignedTickets(this.event.eventId, ticketsRequests).pipe(
        finalize(() => {
          this.ticketTypeService.typesLoading = false;
          this.cartService.isLoading = false;
        })
      ).subscribe((response: TicketsResponse) => {
        this.cartService.lastEventId = this.event?.eventId;
        this.cartService.cart = response.cart;
        this.cartService.eventPricing = response.pricing;
        this.ticketTypeService.resetTicketTypeCounts();
        this.updateCustomerScreen();
        this.updateCart.emit();
      });
    }
  }

  public get hasAnyTicketsSelected(): boolean {
    return Object.values(this.ticketTypeCountMap).some((value: number) => value > 0);
  }
  public get areAnyInputValuesInvalid(): boolean {
    return this.eventPricing.ticketTypes.some((type: EventPricingTicketType) => {
      const available: number = this.getMaxTickets(type);
      return this.ticketTypeCountMap[type.uuid] > available;
    });
  }

  public get hasNoAvailablePrices(): boolean {
    return !this.eventPricing.ticketTypes?.length || !this.eventPricing.available ||
      this.eventPricing.ticketTypes.every(type => !this.isTicketTypeAvailable(type));
  }

  public isTicketTypeAvailable(ticketType: EventPricingTicketType): boolean {
    const now = moment.utc(new Date());
    const availabilityStart = moment.utc(ticketType.availabilityPeriod?.start || null);
    const availabilityEnd = moment.utc(ticketType.availabilityPeriod?.end || null);
    return this.hasEventSaleStarted && (ticketType.available == null || ticketType.available > 0) &&
      (!ticketType.availabilityPeriod || (availabilityStart.isSameOrBefore(now) && availabilityEnd.isSameOrAfter(now)));
  }

  public getRemainingQuantity(ticketType: EventPricingTicketType): number {
    return ticketType.available != null ? (Math.min(ticketType.available, (this.eventPricing.available || 0))) : (this.eventPricing.available || 0)
  }
}
